import React, {
  useState, useEffect, useContext,
  useCallback,
} from 'react';
import { Button, ErrorMessage } from 'anf-core-react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import ORDER_SUBMIT_MUTATION from '../../../gql/orderSubmit.gql';
import useSwitchesFromContext from '../../../hooks/useSwitchesFromContext';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
import handleSubmit from './handleSubmit';
import prepareOrderSubmitData from './prepareOrderSubmitData';
import $window from '../../../tools/window';
import ThreeDsChallengeForm from './ThreeDsChallengeForm';
import {
  FRANCHISE_COUNTRY_REPUDIATE,
  PAYMENT_CURRENCY_GIFTCARD_REPUDIATE,
  PAYMENT_CURRENCY_PAYMENTMETHOD_REPUDIATE,
} from '../../../tools/constants';
import useOcnRedirection from '../../../hooks/useOcnRedirection';
import { isPageUrlContains } from '../../../tools/pageHelper';
import useLog from '../../useLog/useLog';

function SubmitButton({ submitButtonData, paymentInfo, isKlarnaReady }) {
  const { getSwitchValue } = useSwitchesFromContext();
  const hasCheckoutSubmitOverlay = getSwitchValue('chkSubmitOverlay');
  const hasApplePayPXP = getSwitchValue('chkHasApplePayPxp');
  const hasCODSubmitButtonInMfe = getSwitchValue('useMfeSubmitButtonForCodPayment');
  const hasAPMSubmitButtonInMfe = getSwitchValue('useMfeSubmitButtonForApmPayment');
  const hasGooglePaySubmitButtonInMfe = getSwitchValue('chkHasMfeSubmitButtonForGooglepayPayment');
  const hasPayPalSubmitButtonInMfe = getSwitchValue('chkHasMfeSubmitButtonForPaypalPayment');
  const hasStandardPaymentSubmitButtonInMfe = getSwitchValue('chkHasMfeSubmitButtonForStandardPayment');
  const hasGooglePayUpdatedScript = getSwitchValue('hasGooglePay_2_1_0_Script');
  const { checkoutPageState, updateCheckoutPageState } = useContext(CheckoutPageContext);
  const isPayPalExpressCheckout = isPageUrlContains('/ProcessPayPalReturnAndOrderExpress') && checkoutPageState?.cartPayment;

  const {
    label,
    looksGoodPaypalLabel,
    continueWithGooglePayLabel,
    tryAgainLabel,
    processingLabel,
    successLabel,
    unavailableMessage,
    genericOrderSubmitError,
  } = submitButtonData;
  const [displaySubmitButton, setDisplaySubmitButton] = useState(false);
  const [errorMessageText, setErrorMessageText] = useState('');
  const [buttonState, setButtonState] = useState('initial');
  const [currentPaymentType, setCurrentPaymentType] = useState('');
  const [isUsingExpressPayment, setIsUsingExpressPayment] = useState(false);
  const [threeDsInfo, setThreeDsInfo] = useState(null);
  const redirectToOcn = useOcnRedirection();
  const logger = useLog('checkout.SubmitButton');

  const triggerError = useCallback((errorMessage) => {
    if (isUsingExpressPayment) {
      const newEvent = new Event('mfe:submit:error');
      newEvent.detail = errorMessage;
      $window.dispatchEvent(newEvent);
    } else {
      setErrorMessageText(errorMessage);
    }
  }, [isUsingExpressPayment]);

  const clearErrors = useCallback(() => {
    setErrorMessageText('');
    const newEvent = new Event('mfe:applepay:errorClear');
    $window.dispatchEvent(newEvent);
  }, []);

  useEffect(() => {
    if (hasCheckoutSubmitOverlay) {
      const body = document.querySelector('body');
      if (buttonState === 'processing') {
        body.setAttribute('aria-hidden', 'true');
        body.setAttribute('aria-live', 'polite');
      } else {
        body.setAttribute('aria-hidden', 'false');
        body.setAttribute('aria-live', 'off');
      }
    }
  }, [buttonState, hasCheckoutSubmitOverlay]);

  const [orderSubmit] = useMutation(ORDER_SUBMIT_MUTATION, {
    onCompleted: (data) => {
      const paymentURL = data?.orderSubmit?.paymentURL;
      const paymentStatusURL = data?.orderSubmit?.paymentStatusURL;
      updateCheckoutPageState({ orderSubmit: data?.orderSubmit });
      clearErrors();

      if (data?.orderSubmit?.success) {
        if (paymentURL) {
          // for PayPal trigger mfe:paypal:openSheet event (listened in PayPal.jsx)
          // to open payment modal
          if (paymentURL?.indexOf('paypal') !== -1) {
            const event = new CustomEvent('mfe:paypal:openSheet', {
              detail: {
                paymentURL,
                paymentStatusURL,
              },
            });
            $window.dispatchEvent(event);
          } else {
            // for APM redirect user to payment gateway to complete payment
            $window.location = data.orderSubmit.paymentURL;
          }
        } else if (
          // for Credit Card payment 3DS flow an iframe will open
          // with challenge for in redirect URL
          data?.orderSubmit?.isThreeDSEnabledV2
          && data?.orderSubmit?.paymentStatus === 'redirect'
          && data?.orderSubmit?.redirectUrl
        ) {
          setThreeDsInfo({
            orderId: paymentInfo?.orderId,
            redirectUrl: data?.orderSubmit?.redirectUrl ?? '',
            id: data?.orderSubmit?.threeDsPxpId ?? '',
            paymentStatusURL: data?.orderSubmit?.paymentStatusURL ?? '',
          });
        } else {
          setButtonState('success');
          // trigger an event for CRS to listen to and submit the form
          // this evenet will pass the data that was returned from CART
          // currently required for proper redirect to order confirmation page
          redirectToOcn(data?.orderSubmit);
        }
      } else {
        // TODO: trigger an event for CRS to listen to and
        // highlight the fields that have errors
        setButtonState('retry');
        const { statusMessages = [], repudiationData } = data?.orderSubmit || {};
        let errorMessage;
        if (repudiationData) {
          // if repudiation data is present, check the repudiation type
          // and trigger an event for CRS to listen to and display the error message
          // if the repudiation type is PAYMENT_CURRENCY_GIFTCARD_REPUDIATE
          // or PAYMENT_CURRENCY_PAYMENTMETHOD_REPUDIATE
          const repudiationType = repudiationData?.repudiationType;
          if (
            repudiationType === PAYMENT_CURRENCY_GIFTCARD_REPUDIATE
            || repudiationType === PAYMENT_CURRENCY_PAYMENTMETHOD_REPUDIATE
          ) {
            // trigger an event for CRS to listen to and display the error message
            const newEvent = new CustomEvent('mfe:giftCard:repudiation', {
              detail: {
                errorCode: repudiationType,
                errorKey: repudiationData?.error?.key,
                errorMessage: repudiationData?.error?.value,
                params: repudiationData?.params,
              },
            });
            window.dispatchEvent(newEvent);
            return;
          }
          // if the repudiation type is FRANCHISE_COUNTRY_REPUDIATE
          // we don't want to trigger an event, just return
          // as the error message will be displayed in the
          // FranchiseCountryRepudiationError component
          if (repudiationType === FRANCHISE_COUNTRY_REPUDIATE) {
            return;
          }
          // if the repudiation type is not FRANCHISE_COUNTRY_REPUDIATE
          // or PAYMENT_CURRENCY_GIFTCARD_REPUDIATE
          // or PAYMENT_CURRENCY_PAYMENTMETHOD_REPUDIATE
          // we will display the error message
          errorMessage = repudiationData?.error?.value;
        } else if (statusMessages?.length > 0) {
          // if there are status messages, we will display error message
          // from the first status message
          errorMessage = statusMessages[0].message;
        } else {
          // if there are no status messages, we will display a generic error message
          errorMessage = genericOrderSubmitError ?? 'Something went wrong. Please try again.';
        }
        triggerError(errorMessage);
      }
    },
    onError: (error) => {
      logger.error(`ERR: ORDER_SUBMIT_MUTATION: ${JSON.stringify(error?.message)}`);
      setButtonState('retry');

      const errorMessage = genericOrderSubmitError ?? 'Something went wrong. Please try again.';
      triggerError(errorMessage);
    },
  });

  // handle custom submit hook events for state changes and error handling
  useEffect(() => {
    function handleOrderPaySubmit() {
      // clear error message on submit
      clearErrors('');
    }

    // set error message on submit error
    function handleOrderSubmitError(event) {
      const { message } = event.detail;
      setButtonState('retry');

      triggerError(message);
    }

    // redirect to order confirmation page
    function handleOrderSubmitSuccess(event) {
      const data = event?.detail;
      setButtonState('success');

      // trigger an event for CRS to listen to and submit the form
      // this evenet will pass the data that was returned from CART
      // currently required for proper redirect to order confirmation page
      redirectToOcn(data);
    }

    $window.addEventListener('mfe:order:submit', handleOrderPaySubmit);
    $window.addEventListener('mfe:orderSubmit:error', handleOrderSubmitError);
    $window.addEventListener('mfe:orderSubmit:success', handleOrderSubmitSuccess);
    return () => {
      $window.removeEventListener('mfe:orderSubmit:error', handleOrderSubmitError);
      $window.removeEventListener('mfe:order:submit', handleOrderPaySubmit);
      $window.removeEventListener('mfe:orderSubmit:success', handleOrderSubmitSuccess);
    };
  }, [clearErrors, triggerError, setButtonState, redirectToOcn]);

  const handlePaymentSubmit = () => {
    const isPaymentReady = currentPaymentType === 'klarna' ? isKlarnaReady : true;

    handleSubmit({
      currentPaymentType,
      isPaymentReady,
      setErrorMessageText,
      paymentUnavailableMessage: unavailableMessage?.value,
    });
  };

  // listen for the event that CRS will trigger when it's ready to submit
  useEffect(() => {
    async function handleCheckoutSubmitEvent(event) {
      const { success, errorMessage, formData } = event.detail;
      const { authorizationToken, orderId } = paymentInfo;
      if (success) {
        setErrorMessageText('');
        setButtonState('processing');
        const submitData = await prepareOrderSubmitData(formData, checkoutPageState);
        const paymentType = isPayPalExpressCheckout ? 'paypal' : currentPaymentType;
        const currentPaymentInfo = {
          orderId,
          paymentType,
        };

        if (currentPaymentType === 'klarna') {
          currentPaymentInfo.authorizationToken = authorizationToken;
        }
        updateCheckoutPageState({ orderSubmit: null });
        orderSubmit({
          variables: {
            orderSubmitInput: submitData,
            paymentInfo: currentPaymentInfo,
          },
        });
      } else {
        setErrorMessageText(errorMessage);
        setButtonState('initial');
      }
    }
    $window.addEventListener('mfe:checkout:submit', handleCheckoutSubmitEvent);
    return () => {
      $window.removeEventListener('mfe:checkout:submit', handleCheckoutSubmitEvent);
    };
  }, [paymentInfo,
    orderSubmit,
    checkoutPageState,
    currentPaymentType,
    isPayPalExpressCheckout,
    updateCheckoutPageState]);

  useEffect(() => {
    const handleCheckoutGooglePayPaymentSubmit = (event) => {
      const { formData = {} } = event?.detail;
      if (currentPaymentType === 'googlepay') {
        let handlePaymentSuccess = () => {};
        let handlePaymentError = () => {};

        setButtonState('processing');

        handlePaymentSuccess = (successEvent) => {
          // remove event listener
          $window.removeEventListener('googlePay:success', handlePaymentSuccess);
          $window.removeEventListener('googlePay:error', handlePaymentError);
          // get the cart api ready data from the event
          const { detail: cartAPIReadyData = {} } = successEvent;
          // merge the google pay data with the form data
          const updatedFormData = {
            ...formData,
            ...cartAPIReadyData,
            payment: {
              ...formData?.payment,
              ...cartAPIReadyData?.payment,
            },
          };
          // submit the form
          const customEvent = new Event('mfe:checkout:submit');
          customEvent.detail = {
            success: true,
            formData: updatedFormData,
          };
          $window.dispatchEvent(customEvent);
        };

        handlePaymentError = () => {
          // Here, merchant can do anything they want with the error
          $window.removeEventListener('googlePay:success', handlePaymentSuccess);
          $window.removeEventListener('googlePay:error', handlePaymentError);

          // reset the submit button and display error message
          setButtonState('initial');
          triggerError(genericOrderSubmitError);
        };

        // handle success scenario
        $window.addEventListener('googlePay:success', handlePaymentSuccess);
        // handle error scenario
        $window.addEventListener('googlePay:error', handlePaymentError);

        // open google pay sheet
        if (hasGooglePayUpdatedScript) {
          if (typeof onGooglePaymentButtonClicked === 'function') {
            // ignore no-under eslint rule, this function will come for pxp gp.js file
            // eslint-disable-next-line no-undef
            onGooglePaymentButtonClicked();
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          if (typeof onMerchantButtonClick === 'function') {
            // ignore no-under eslint rule, this function will come for pxp gp.js file
            // eslint-disable-next-line no-undef
            onMerchantButtonClick();
          }
        }
      }
    };

    $window.addEventListener('crs:googlePayPayment:authorize', handleCheckoutGooglePayPaymentSubmit);
    return () => {
      $window.removeEventListener('crs:googlePayPayment:authorize', handleCheckoutGooglePayPaymentSubmit);
    };
  }, [
    hasGooglePayUpdatedScript,
    currentPaymentType,
    genericOrderSubmitError,
    triggerError,
  ]);

  // listen for the CRS payment selector payment method change event
  // and if it's klarna or apple pay (PXP flow only), show the MFE submit button
  useEffect(() => {
    function handlePaymentMethodChangeEvent(event) {
      const { paymentType } = event.detail;
      if (paymentType === 'klarna' || (hasApplePayPXP && paymentType === 'applepay')) {
        setDisplaySubmitButton(true);
        setCurrentPaymentType(paymentType);
      } else {
        setDisplaySubmitButton(false);
        setCurrentPaymentType('');
      }
    }
    $window.addEventListener('paymentSection:paymentUpdated', handlePaymentMethodChangeEvent);
    return () => {
      $window.removeEventListener('paymentSection:paymentUpdated', handlePaymentMethodChangeEvent);
    };
  }, [hasApplePayPXP]);

  // on MFE payment selector payment method change
  // and if it's klarna or apple pay (PXP flow only), show the MFE submit button
  useEffect(() => {
    const paymentType = checkoutPageState?.selectedPaymentType;
    if (paymentType === 'klarna'
      || (hasApplePayPXP && paymentType === 'applepay')
      || (hasAPMSubmitButtonInMfe && paymentType === 'apm')
      || (hasCODSubmitButtonInMfe && paymentType === 'cod')
      || (hasGooglePaySubmitButtonInMfe && paymentType === 'googlepay')
      || (hasPayPalSubmitButtonInMfe && paymentType === 'paypal')
      || (hasPayPalSubmitButtonInMfe && !paymentType && isPayPalExpressCheckout)
      || (hasStandardPaymentSubmitButtonInMfe && !isPayPalExpressCheckout && (paymentType === 'credit card' || !paymentType))
    ) {
      setDisplaySubmitButton(true);
      setCurrentPaymentType(paymentType);
    } else {
      setDisplaySubmitButton(false);
      setCurrentPaymentType('');
    }
  }, [
    checkoutPageState?.selectedPaymentType,
    hasApplePayPXP,
    hasAPMSubmitButtonInMfe,
    hasCODSubmitButtonInMfe,
    hasGooglePaySubmitButtonInMfe,
    hasPayPalSubmitButtonInMfe,
    hasStandardPaymentSubmitButtonInMfe,
    isPayPalExpressCheckout,
  ]);

  // listen to event that will let us know if it's express apple pay or not
  // depending on that we will show an error on the header or above submit button
  useEffect(() => {
    function handleApplePayExpressEvent(event) {
      const { isApplePayExpress } = event.detail;
      setIsUsingExpressPayment(isApplePayExpress);
      clearErrors();
    }
    $window.addEventListener('mfe:checkout:applePayOpen', handleApplePayExpressEvent);
    return () => {
      $window.removeEventListener('mfe:checkout:applePayOpen', handleApplePayExpressEvent);
    };
  }, [setIsUsingExpressPayment, clearErrors]);

  // we don't want to show the MFE submit button until Klarna or Apple Pay is selected
  // until more payments implemented
  if (!displaySubmitButton) {
    return null;
  }

  const getButtonLabel = (currentState) => {
    const paymentType = checkoutPageState?.selectedPaymentType;
    let initialLabel = label.value;
    if (paymentType === 'paypal') {
      initialLabel = looksGoodPaypalLabel.value;
    } else if (paymentType === 'googlepay') {
      initialLabel = continueWithGooglePayLabel.value;
    }
    switch (currentState) {
      case 'initial':
        return initialLabel;
      case 'processing':
        return processingLabel.value;
      case 'retry':
        return tryAgainLabel.value;
      case 'success':
        return successLabel.value;
      default:
        return label.value;
    }
  };

  return (
    <div className="submit-button-wrapper-mfe" data-testid="submit-button-wrapper-mfe">
      {(buttonState === 'processing' && hasCheckoutSubmitOverlay) && <div className="submit-overlay" />}
      {errorMessageText
        && (
        <ErrorMessage id="summary-error-message">
          {errorMessageText}
        </ErrorMessage>
        )}
      <Button
        classList="submit-button"
        variant="primary"
        onClick={handlePaymentSubmit}
        isProcessing={buttonState === 'processing'}
        isDisabled={buttonState === 'processing'}
      >
        {getButtonLabel(buttonState)}
      </Button>
      {threeDsInfo && (
        <ThreeDsChallengeForm
          threeDsInfo={threeDsInfo}
          threeDsMessage={checkoutPageState?.textFor?.threeDsAnfMessage}
          closeButtonLabel={checkoutPageState?.textFor?.closeButton}
          onSuccess={() => {
            setButtonState('success');
            redirectToOcn();
            setThreeDsInfo(null);
          }}
          onFailure={(errorMessage) => {
            triggerError(errorMessage);
            setButtonState('retry');
            setThreeDsInfo(null);
          }}
          onCancel={() => {
            setButtonState('initial');
            setThreeDsInfo(null);
          }}
        />
      )}
    </div>
  );
}

SubmitButton.defaultProps = {
  submitButtonData: {
    label: {
      key: 'Submit',
      value: 'Submit Order',
    },
    looksGoodPaypalLabel: {
      key: 'CHK_LOOKS_GOOD_TAKE_ME_TO_PAYPAL',
      value: 'Looks Good, Take Me to PayPal',
    },
    continueWithGooglePayLabel: {
      key: 'CHK_GOOGLE_PAY_SUBMIT_LABEL',
      value: 'Continue with Google Pay',
    },
    tryAgainLabel: {
      key: 'Try_Again',
      value: 'Try Placing Order Again',
    },
    processingLabel: {
      key: 'Processing',
      value: 'Processing',
    },
    successLabel: {
      key: 'Success',
      value: 'Success',
    },
    genericOrderSubmitError: 'Something went wrong. Please try again.',
  },
  isKlarnaReady: false,
};

SubmitButton.propTypes = {
  submitButtonData: PropTypes.shape({
    label: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    looksGoodPaypalLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    continueWithGooglePayLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    tryAgainLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    processingLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    successLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    unavailableMessage: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    genericOrderSubmitError: PropTypes.string,
  }),
  paymentInfo: PropTypes.shape({
    paymentType: PropTypes.string,
    authorizationToken: PropTypes.string,
    orderId: PropTypes.string,
  }).isRequired,
  isKlarnaReady: PropTypes.bool,
};

export default SubmitButton;
